import { NotificationUnsupportedError } from '@/features/notifications/errors';
import type { Platform } from '@/shared/utils/platform';
import { isNativePlatform } from '@/shared/utils/platform';

import { checkNativePermissions, getNativeToken } from './nativePush';
import { checkWebPermissions, getWebToken } from './webPush';

export interface GetTokenOptions {
  /**
   * If true, only checks if permission is already granted (no popup).
   * If false (default), requests permission from user (shows system popup).
   */
  silent?: boolean;
}

/**
 * Get FCM token for the specified platform.
 *
 * @param platform - Target platform (WEB, IOS, ANDROID)
 * @param options
 * @param options.silent - If true, only proceeds if permission already granted.
 *                         Use for background token refresh. Default: false.
 * @returns The FCM token.
 * @throws {NotificationPermissionDeniedError} If permission was denied.
 * @throws {NotificationBrowserBlockedError} If web notifications are blocked.
 * @throws {NotificationTokenTimeoutError} If native token registration timed out.
 * @throws {NotificationUnsupportedError} If platform doesn't support push.
 */
export async function getPushToken(
  platform: Platform,
  options?: GetTokenOptions,
): Promise<string> {
  if (platform === 'WEB') {
    return getWebToken(options);
  }

  if (isNativePlatform()) {
    return getNativeToken(options);
  }

  throw new NotificationUnsupportedError(
    `Push notifications are not supported on platform: ${platform}`,
  );
}

/**
 * Check if push notifications are supported on current platform
 */
export function isPushSupported(): boolean {
  if (isNativePlatform()) {
    return true;
  }

  return 'Notification' in window && 'serviceWorker' in navigator;
}

/**
 * Check current push notification permission status for the current platform
 * Returns true if permissions are granted, false otherwise
 */
export async function checkPermissions(): Promise<boolean> {
  if (isNativePlatform()) {
    return checkNativePermissions();
  }

  return checkWebPermissions();
}
